import React, { useMemo, useRef, useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Loader } from '@progress/kendo-react-indicators';
import dayjs from 'dayjs';
import { CreatingField } from '../../../components/CreatingField';
import VehicleAutoComplete from '../../AssetVehicles/VehicleAutoComplete';
import TrailerAutoComplete from '../../AssetTrailers/TrailerAutoComplete';
import type { TrailerValue } from '../../AssetTrailers/TrailerAutoComplete';
import { fetchApi } from '../../../services/api';
import { JsonResponse } from 'TypeGen/json-response';

type Props = {
  onClose: () => void;
  onCreated: (workOrderId?: number) => void;
};

enum WorkOrderPriority {
  Low = 1,
  Normal = 5,
  High = 10,
  Critical = 20,
}

type PriorityOption = {
  text: string;
  value: WorkOrderPriority;
};

const priorityOptions: PriorityOption[] = [
  { text: 'Low', value: WorkOrderPriority.Low },
  { text: 'Normal', value: WorkOrderPriority.Normal },
  { text: 'High', value: WorkOrderPriority.High },
  { text: 'Critical', value: WorkOrderPriority.Critical },
];

type FormState = {
  VehicleID: number | null;
  TrailerID: number | null;
  Priority: WorkOrderPriority;
  APUHours: number | null;
  ChassisMiles: number | null;
  ScheduledStartDate: Date | null;
  Description: string;
};

const defaultFormState: FormState = {
  VehicleID: null,
  TrailerID: null,
  Priority: WorkOrderPriority.Normal,
  APUHours: null,
  ChassisMiles: null,
  ScheduledStartDate: null,
  Description: '',
};

type VehicleOdometerResponse = {
  OdometerMiles: number | null;
};

const CreateWorkOrderDialog: React.FC<Props> = ({ onClose, onCreated }) => {
  const [form, setForm] = useState<FormState>({ ...defaultFormState });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pendingChassisMilesRequest = useRef<number | null>(null);
  const hasManualChassisMilesOverride = useRef(false);

  const resetForm = () => {
    setForm({ ...defaultFormState });
    setError(null);
    setSaving(false);
  };

  const resetAndClose = () => {
    resetForm();
    onClose();
  };

  const canSave = useMemo(() => {
    return !saving;
  }, [saving]);

  const prefillChassisMiles = async (vehicleId: number) => {
    pendingChassisMilesRequest.current = vehicleId;
    try {
      const response = await fetchApi(`/api/ShopWorkOrder/VehicleChassisMiles/${vehicleId}`) as VehicleOdometerResponse;
      if (pendingChassisMilesRequest.current !== vehicleId || hasManualChassisMilesOverride.current) {
        return;
      }

      const newChassisMiles = typeof response?.OdometerMiles === 'number' ? response.OdometerMiles : null;
      setForm((prev) => ({ ...prev, ChassisMiles: newChassisMiles }));
    } catch (err) {
      if (pendingChassisMilesRequest.current === vehicleId) {
        console.error('Failed to prefill chassis miles.', err);
      }
    } finally {
      if (pendingChassisMilesRequest.current === vehicleId) {
        pendingChassisMilesRequest.current = null;
      }
    }
  };

  const handleSave = async () => {
    if (!canSave) {
      return;
    }

    setSaving(true);
    setError(null);

    const payload = {
      VehicleID: form.VehicleID ?? null,
      TrailerID: form.TrailerID ?? null,
      Priority: form.Priority,
      APUHours: form.APUHours ?? null,
      ChassisMiles: form.ChassisMiles ?? null,
      ScheduledStartDate: form.ScheduledStartDate ? dayjs(form.ScheduledStartDate).format('YYYY-MM-DD') : null,
      Description: form.Description.trim().length ? form.Description.trim() : null,
    };

    try {
      const response: JsonResponse = await fetchApi('/api/ShopWorkOrder/CreateWorkOrder', payload, 'POST') as JsonResponse;
      if (!response.Success) {
        setError(response.ErrorMessage || 'Unable to create work order.');
        setSaving(false);
        return;
      }

      const createdLink = response.Links.find((link) => link?.Name === 'WorkOrder');
      const segments = createdLink.Link.split('/');

      resetForm();
      onCreated(Number(segments[segments.length - 1]));
    } catch (err) {
      console.error('Failed to create work order.', err);
      setError('Unable to create work order.');
      setSaving(false);
    }
  };

  return (
    <Dialog title="Create Work Order" onClose={resetAndClose} className="dialog-w800">
      <div className="k-form k-form-md">
        {saving && (
          <div className="mb-3 d-flex justify-content-center">
            <Loader type="converging-spinner" />
          </div>
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">Vehicle</label>
            <VehicleAutoComplete
              selectedVehicleID={form.VehicleID ?? undefined}
              onSelectedVehicle={(vehicle) => {
                const vehicleId = vehicle?.VehicleID ?? null;
                hasManualChassisMilesOverride.current = false;
                setForm((prev) => ({ ...prev, VehicleID: vehicleId, ChassisMiles: null }));
                if (vehicleId) {
                  prefillChassisMiles(vehicleId);
                } else {
                  pendingChassisMilesRequest.current = null;
                }
              }}
              disabled={saving}
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">Trailer</label>
            <TrailerAutoComplete
              selectedTrailerID={form.TrailerID ?? undefined}
              onSelectedTrailer={(trailer) => {
                const trailerId = (trailer as TrailerValue | null)?.TrailerID ?? null;
                setForm((prev) => ({ ...prev, TrailerID: trailerId }));
              }}
              disabled={saving}
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">Priority</label>
            <DropDownList
              data={priorityOptions}
              textField="text"
              dataItemKey="value"
              value={priorityOptions.find((option) => option.value === form.Priority) ?? priorityOptions[0]}
              onChange={(e) => setForm((prev) => ({ ...prev, Priority: e.target.value.value as WorkOrderPriority }))}
              disabled={saving}
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-md-3 mb-3">
            <label className="form-label fw-bold">APU Hours</label>
            <CreatingField
              dataType="number"
              data={form.APUHours}
              inputProps={{ format: 'n1', min: 0, step: 0.5, disabled: saving, placeholder: 'Optional' }}
              onChange={(value) => setForm((prev) => ({ ...prev, APUHours: value ?? null }))}
            />
          </div>
          <div className="col-md-3 mb-3">
            <label className="form-label fw-bold">Chassis Miles</label>
            <CreatingField
              dataType="number"
              data={form.ChassisMiles}
              inputProps={{ format: 'n1', min: 0, disabled: saving, placeholder: 'Optional' }}
              onChange={(value) => {
                hasManualChassisMilesOverride.current = true;
                setForm((prev) => ({ ...prev, ChassisMiles: value ?? null }));
              }}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">Scheduled Start</label>
            <CreatingField
              dataType="date"
              data={form.ScheduledStartDate}
              onChange={(value) => setForm((prev) => ({ ...prev, ScheduledStartDate: value ?? null }))}
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Customer Concern</label>
          <CreatingField
            data={form.Description}
            renderEditable={(value, onChange) => (
              <textarea
                className="form-control"
                maxLength={500}
                rows={3}
                disabled={saving}
                value={value}
                onChange={(e) => onChange(e.target.value)}
              />
            )}
            onChange={(value) => setForm((prev) => ({ ...prev, Description: value }))}
          />
        </div>

      </div>
      <DialogActionsBar>
        <Button type="button" onClick={resetAndClose} disabled={saving}>
          Cancel
        </Button>
        <Button type="button" themeColor="primary" disabled={!canSave} onClick={handleSave}>
          Create Work Order
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default CreateWorkOrderDialog;
